import React, { useCallback, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import Like from '../../../../assets/images/svg/likeEmpty.svg'
import LikeFilled from '../../../../assets/images/svg/like.svg'
import BTS from '../../../../assets/images/svg/BTS.svg'
import { getInitials, isTeamMessage } from '../../utils'
import { MessageWithContent, TeamMessage, SanityLink, SanityLinkType } from '../../types'
import { UnreadAmount } from './tabs'
import { PortableText } from '@portabletext/react'
import { likeMessage, readMessage, unlikeMessage } from '../../data'
import LinkButton from '../../../../components/LinkButton/LinkButton'
import useGame from '../../../../contexts/GameContext/useGame'
import { useSurvey } from '../../../../hooks/useSurvey/useSurvey'
import theme from '../../../../styles/theme'
import { getInternalRoute } from '../../routes'

const TeamLogo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.lightGrey};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 17px;
  font-weight: 700;
  color: black;
`

const MessageImage = styled.img`
  width: 100%;
  object-fit: contain;
  border-radius: 15px;
  margin: 20px 0;
`

const LinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`

const TriggerButton = styled.button`
  &:hover {
    border-radius: 0;
    border-top-left-radius: 30px;
    border-top-right-radius: 30px;
    background-color: ${theme.colors.lightGrey} !important;
  }
`

const LikeButton = styled.button`
  &:hover {
    border-radius: 30px;
    background-color: ${theme.colors.grey} !important;
  }
`

function getLinkUrl(
  link: SanityLink,
  params: {
    twitterUsername?: string | null
    facebookUsername?: string | null
    surveyLink?: string | null
  },
): string | null {
  switch (link.type) {
    case SanityLinkType.External:
      return link.url
    case SanityLinkType.Internal:
      return getInternalRoute(link.path)
    case SanityLinkType.Event:
      return `/events/${link.event.eventSlug}`
    case SanityLinkType.Survey:
      return params.surveyLink || null
    case SanityLinkType.Social:
      const username = link.social === 'x' ? params.twitterUsername : params.facebookUsername
      return username ? `https://${link.social === 'x' ? 'twitter.com' : 'facebook.com'}/${username}` : null
    default:
      return null
  }
}

interface Props {
  message: MessageWithContent | TeamMessage
}

const READ_TIMER_MS: number = 1000 as const

export function MessageCard({ message }: Props) {
  const game = useGame()
  const { surveyLink } = useSurvey()

  const messageRef = useRef<HTMLDivElement>(null)

  const timeDiff = formatDistanceToNow(new Date(message.createdAt))
  const teamLayout = isTeamMessage(message)

  const { twitterUsername, facebookUsername } = game.game?.content || {}

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isLiked, setIsLiked] = useState<boolean>(message.isLiked)
  const [likesCount, setLikesCount] = useState<number>(message.likesCount)
  const [isRead, setIsRead] = useState<boolean>(!!message.readAt)

  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  const like = useCallback(() => {
    setIsLiked(true)
    setLikesCount((prev) => prev + 1)
    likeMessage({ campaignId: message.campaignId }).catch(() => {
      setIsLiked(false)
      setLikesCount((prev) => prev - 1)
    })
  }, [message.campaignId])

  const unlike = useCallback(() => {
    setIsLiked(false)
    setLikesCount((prev) => prev - 1)
    unlikeMessage({ campaignId: message.campaignId }).catch(() => {
      setIsLiked(true)
      setLikesCount((prev) => prev + 1)
    })
  }, [message.campaignId])

  const getLink = useCallback(
    (link?: SanityLink, secondary: boolean = false) => {
      if (!link) return null

      const isSocial = link.type === SanityLinkType.Social
      const isExternal = link.type === SanityLinkType.External
      const isSurvey = link.type === SanityLinkType.Survey

      if (link.type === SanityLinkType.Social) {
        if (link.social === 'x' && !twitterUsername) return null
        if (link.social === 'facebook' && !facebookUsername) return null
      }

      const url = getLinkUrl(link, { twitterUsername, facebookUsername, surveyLink })
      if (!url) return null

      return (
        <LinkButton
          to={url}
          external={isSocial || isExternal || isSurvey}
          $secondary={!secondary}
          $tertiary={secondary}
        >
          {link.label}
        </LinkButton>
      )
    },
    [twitterUsername, facebookUsername, surveyLink],
  )

  useEffect(() => {
    if (isOpen && !isRead) {
      const timer = setTimeout(() => {
        setIsRead(true)
        readMessage({ messageId: message.messageId }).catch(() => setIsRead(false))
      }, READ_TIMER_MS)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [isOpen, message.messageId, isRead])

  useEffect(() => {
    if (isOpen && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isOpen])

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: theme.colors.white,
        borderRadius: '30px',
        boxShadow: '0 40px 100px 0 #0000000d',
      }}
      ref={messageRef}
    >
      <TriggerButton
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          border: 'none',
          background: 'none',
          padding: 20,
          width: '100%',
        }}
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={`message-content-${message.messageId}`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {teamLayout ? <TeamLogo>{getInitials(message.teamName)}</TeamLogo> : <BTS />}
          <p style={{ margin: 0, fontWeight: 'bold', color: theme.colors.black }}>
            {teamLayout ? message.teamName : 'BTS'}
          </p>
          {!isRead && !isOpen && <UnreadAmount>New</UnreadAmount>}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ fontSize: 20, fontWeight: 'bold', color: theme.colors.black, margin: 0 }}>
              {teamLayout ? message.title : message.sanity.title}
            </p>
            {!teamLayout && message.sanity.subtitle && (
              <p style={{ fontSize: 16, color: theme.colors.midGrey }}>{message.sanity.subtitle}</p>
            )}
          </div>
          {!teamLayout && message.sanity.category.icon.asset?.url && (
            <img
              style={{ width: 50, overflow: 'hidden', objectFit: 'cover' }}
              src={message.sanity.category.icon.asset.url}
              alt="category icon"
            />
          )}
        </div>
      </TriggerButton>
      <div
        id={`message-content-${message.messageId}`}
        style={{
          paddingRight: 20,
          paddingLeft: 20,
          textAlign: 'left',
          maxHeight: isOpen ? '2000px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
          opacity: isOpen ? 1 : 0,
        }}
        role="region"
        aria-label={`Message content from ${teamLayout ? message.teamName : 'BTS'}`}
      >
        {!teamLayout && message.sanity.imagePosition === 'before' && (
          <MessageImage
            src={message.sanity.image?.asset.url || message.sanity.category.image.asset.url}
            alt="message content"
          />
        )}
        {teamLayout ? message.body : <PortableText value={message.sanity.body} />}
        {!teamLayout && message.sanity.imagePosition === 'after' && (
          <MessageImage
            src={message.sanity.image?.asset.url || message.sanity.category.image.asset.url}
            alt="message content"
          />
        )}
        {!teamLayout && (
          <LinkContainer>
            {getLink(message.sanity.link1)}
            {getLink(message.sanity.link2, true)}
          </LinkContainer>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: 20 }}>
        <p style={{ fontSize: 14, color: theme.colors.midGrey }}>{timeDiff} ago</p>
        <LikeButton
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            backgroundColor: theme.colors.lightGrey,
            color: theme.colors.black,
            padding: '5px 10px',
            borderRadius: 30,
          }}
          onClick={isLiked ? unlike : like}
          aria-label={isLiked ? 'Unlike message' : 'Like message'}
        >
          {isLiked ? <LikeFilled /> : <Like />}
          <span style={{ fontSize: 14 }}>{likesCount}</span>
        </LikeButton>
      </div>
    </div>
  )
}
